// Diagnose Mode: Standalone (Mode 1), VDR-invoked (Mode 2), and VDR multi-session (Mode 3). User profile effects.
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { UserSettingService } from '../../../admin-features/services/diagnosis-session/user-setting.service';
import { PslSelectionService } from '../../../admin-features/services/master-data/psl-selection.service';
import {
  PSLActions,
  PSLAPIActions,
  UoMSettingActions,
  UoMSettingAPIActions,
} from '../actions/user-profile.actions';
import { loadDataByDiagnosisId } from '../actions/workflow.actions';
import { RootState } from '../root/root-state.model';
import { selectPSL } from '../selectors/user-profile.selectors';

@Injectable()
export class UserProfileEffects {
  #store = inject(Store<RootState>);
  readonly #actions = inject(Actions);
  readonly #pslSelectionService = inject(PslSelectionService);
  readonly #userSettingService = inject(UserSettingService);

  getCurrentPSL$ = createEffect(() =>
    this.#actions.pipe(
      ofType(PSLActions.getCurrentPSL),
      concatLatestFrom(() => this.#store.select(selectPSL)),
      switchMap(([, psl]) =>
        of(
          PSLActions.setCurrentPSL({
            psl,
          }),
        ),
      ),
    ),
  );

  getPSLBySession$ = createEffect(() =>
    this.#actions.pipe(
      ofType(loadDataByDiagnosisId),
      exhaustMap(({ diagnosisId }) => {
        this.#store.dispatch(PSLAPIActions.setPSLLoadingState());

        return this.#pslSelectionService.getPSLBySession(diagnosisId).pipe(
          map((psl) => PSLAPIActions.getPSLBySessionSuccess({ psl })),
          catchError((error) =>
            of(PSLAPIActions.getPSLBySessionFailure({ error })),
          ),
        );
      }),
    ),
  );

  getAvailablePSLs$ = createEffect(() =>
    this.#actions.pipe(
      ofType(PSLActions.getAvailablePSLs),
      exhaustMap(() =>
        this.#pslSelectionService.getActivePSLs().pipe(
          map((psls) => PSLAPIActions.getAvailablePSLsSuccess({ psls })),
          catchError((error) => of(PSLAPIActions.requestFailure({ error }))),
        ),
      ),
    ),
  );

  loadUoMSetting$ = createEffect(() =>
    this.#actions.pipe(
      ofType(UoMSettingActions.loadUoMSetting),
      exhaustMap(() =>
        this.#userSettingService.getCurrentUoMSetting().pipe(
          map((uomSetting) =>
            UoMSettingAPIActions.loadUoMSettingSuccess({ uomSetting }),
          ),
          catchError((error) =>
            of(UoMSettingAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );

  applyUoMSetting$ = createEffect(() =>
    this.#actions.pipe(
      ofType(UoMSettingActions.applyUoMSetting),
      exhaustMap(({ uomSetting }) =>
        this.#userSettingService.applyUoMSetting(uomSetting).pipe(
          map((appliedSetting) =>
            UoMSettingAPIActions.applyUoMSettingSuccess({
              uomSetting: appliedSetting,
            }),
          ),
          catchError((error) =>
            of(UoMSettingAPIActions.requestFailure({ error })),
          ),
        ),
      ),
    ),
  );
}
